/**
 * ReportsListTab Component
 * Aba de listagem de relatórios disponíveis
 */

import React, { useState, useMemo } from 'react';
import { FileText, Search } from 'lucide-react';
import { ReportCard } from './ReportCard';
import type { ReportDefinition } from '@/services/reports-service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';

interface ReportsListTabProps {
  reports: ReportDefinition[];
  loading: boolean;
  onExecute: (report: ReportDefinition) => void;
  onConfigure?: (report: ReportDefinition) => void;
  isExecuting: (reportCode: string) => boolean;
}

export const ReportsListTab: React.FC<ReportsListTabProps> = ({
  reports,
  loading,
  onExecute,
  onConfigure,
  isExecuting
}) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Filter reports
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesSearch = !search ||
        report.name.toLowerCase().includes(search.toLowerCase()) ||
        report.description.toLowerCase().includes(search.toLowerCase());

      const matchesCategory = !categoryFilter || report.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [reports, search, categoryFilter]);

  // Group reports by category
  const groupedReports = useMemo(() => {
    const groups: Record<string, ReportDefinition[]> = {};

    filteredReports.forEach(report => {
      if (!groups[report.category]) {
        groups[report.category] = [];
      }
      groups[report.category].push(report);
    });

    return groups;
  }, [filteredReports]);

  const categories = [
    { value: '', label: 'Todas as Categorias' },
    { value: 'clients', label: 'Clientes' },
    { value: 'cases', label: 'Casos' },
    { value: 'tasks', label: 'Tarefas' },
    { value: 'finance', label: 'Financeiro' }
  ];

  const getCategoryLabel = (category: string): string => {
    const map: Record<string, string> = {
      'clients': 'CLIENTES',
      'cases': 'CASOS',
      'tasks': 'TAREFAS',
      'finance': 'FINANCEIRO'
    };
    return map[category] || category.toUpperCase();
  };

  // Check if report is new (added in last 7 days)
  const isNewReport = (reportCode: string): boolean => {
    // Mark cases_summary and tasks_productivity as new
    return ['cases_summary', 'tasks_productivity'].includes(reportCode);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <span className="sr-only">Carregando relatórios...</span>
        <Skeleton className="h-16" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-56" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              type="text"
              aria-label="Buscar relatórios"
              placeholder="Buscar relatórios..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-full sm:w-56">
              <NativeSelect
                aria-label="Categoria"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            {(search || categoryFilter) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearch('');
                  setCategoryFilter('');
                }}
              >
                Limpar filtros
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {filteredReports.length} relatório{filteredReports.length !== 1 ? 's' : ''} encontrado{filteredReports.length !== 1 ? 's' : ''}
        </p>
      </div>

      {Object.keys(groupedReports).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <FileText className="size-10 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">Nenhum relatório encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-8">
          {Object.entries(groupedReports).map(([category, categoryReports]) => (
            <section key={category} className="flex flex-col gap-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {getCategoryLabel(category)}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {categoryReports.map(report => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    isNew={isNewReport(report.code)}
                    isExecuting={isExecuting(report.code)}
                    onExecute={() => onExecute(report)}
                    onConfigure={onConfigure ? () => onConfigure(report) : undefined}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
